import { DocumentNode } from 'graphql';
import { useCallback, useEffect, useMemo } from 'react';

import { elevioPublicGqlClient } from '../../components/shared/knowledgeBaseLinkPreview/api';
import { useElevioIntegration } from '../../elevioIntegrationProvider';

import { useAsyncCallState } from './useAsyncCallState';

type Variables = { [key: string]: string | number | Variables };

type Params = {
  query: DocumentNode;
  variables: Variables | undefined;
  companyId?: string;
};

type ReturnType<TQueryResponse> = {
  data: TQueryResponse | null;
  error: Error | null;
  isLoading: boolean;
  triggerFetch: () => Promise<void> | undefined;
};

export function useElevioQuery<TQueryResponse>({
  query,
  variables,
  companyId,
}: Params): ReturnType<TQueryResponse> {
  const { elevioIntegration } = useElevioIntegration();

  const { data, error, isLoading, setData, setError, setIsLoading } =
    useAsyncCallState<TQueryResponse>();

  const triggerFetch = useCallback(() => {
    if (!query) return;
    if (!variables) return;
    if (!elevioIntegration?.companyId && !companyId) return;

    setIsLoading(true);

    return elevioPublicGqlClient
      .request<TQueryResponse>(query, variables, {
        'company-uid': companyId ?? (elevioIntegration?.companyId || ''),
      })
      .then(setData)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [
    companyId,
    elevioIntegration?.companyId,
    query,
    variables,
    setData,
    setError,
    setIsLoading,
  ]);

  useEffect(() => {
    triggerFetch();
  }, [triggerFetch]);

  return useMemo(
    () => ({ data, error, isLoading, triggerFetch }),
    [data, error, isLoading, triggerFetch],
  );
}
